// src/pages/AbsenceOverview.js
import React, { useState, useMemo, useEffect } from "react";
import { Nav } from "../../components/nav";
import { useAuth } from "../../navigation/AuthProvider";
import "../css/Payroll.css";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import { Toast } from "../../components/ToastPopup";
import { calculatePayForEmployee } from "../../utils/calculatePayForEmployee";

export const AbsenceOverview = () => {
  const {
    employees,
    timeSchemas,
    company,
    user,

    // needed for calculatePayForEmployee consistency
    pays,
    vacationPay,
    overtimeRules,
    obRules,
    absenceRules = [],
  } = useAuth();

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  // 🔁 Lokal state för skift
  const [shifts, setShifts] = useState([]);

  // ✅ Karens state (från samma endpoint) – kan vara tomt om backend inte skickar
  const [karensByEmployee, setKarensByEmployee] = useState({});

  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];


  // 🔥 ADD THIS
  const getEmployeeId = (e) => {
    if (!e?.employeeId) return null;
    return typeof e.employeeId === "object"
      ? e.employeeId?._id
      : e.employeeId;
  };

  // strict: only explicit true means enabled
  const karensEnabled = company?.payrollSettings?.karensEnabled === true;

  // mirror OB: only complete shifts with employees
  const isShiftCompleteForPayroll = (s) => {
    const empHasId =
      Array.isArray(s?.employees) &&
      s.employees.some((e) => {
        const id = getEmployeeId(e);
        return !!id;
      });
    return !!(
      s &&
      s.status === "complete" &&
      s.date &&
      typeof s.startTime === "string" &&
      s.startTime.includes(":") &&
      typeof s.endTime === "string" &&
      s.endTime.includes(":") &&
      empHasId
    );
  };

  // fetch shifts for month (+ karens if backend returns it)
  useEffect(() => {
    const fetchShifts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
          params: {
            userId: user._id,
            companyId: company._id,
            selectedMonth,
            selectedYear,
            timeSchemaId: selectedSchema || undefined,
          },
        });

        setShifts(Array.isArray(data.shifts) ? data.shifts : []);
        setKarensByEmployee(
          data && typeof data.karensByEmployee === "object"
            ? data.karensByEmployee
            : {}
        );
      } catch (err) {
        console.error("Nätverksfel eller serverfel:", err);
        const serverMsg =
          err.response?.data?.message || err.response?.data?.errorText;
        setToastType("error");
        setToastMessage(serverMsg || "Kunde inte hämta skift – se konsolen");
      } finally {
        setLoading(false);
      }
    };

    if (company?._id && user?._id) fetchShifts();
  }, [company?._id, selectedMonth, selectedYear, user?._id, selectedSchema]);

  // Filter employees by text
  const filteredEmployees = useMemo(
    () =>
      employees.filter((emp) => {
        const text = searchQuery.toLowerCase();
        return (
          (emp.name || "").toLowerCase().includes(text) ||
          (emp.email || "").toLowerCase().includes(text) ||
          (emp.role || "").toLowerCase().includes(text)
        );
      }),
    [employees, searchQuery]
  );

  // Build absence columns from ALL absenceRules
  // ✅ Exclude "sick" from mapped headers (since sick is shown separately already)
  const absenceColumns = useMemo(() => {
    if (Array.isArray(absenceRules) && absenceRules.length > 0) {
      return absenceRules
        .filter((r) => (r?.systemKey || null) !== "sick")
        .map((r) => ({
          id: String(r._id),
          name: r.name || String(r._id),
          systemKey: r.systemKey || null,
        }));
    }

    const set = new Set();
    shifts.forEach((s) => {
      if (!isShiftCompleteForPayroll(s)) return;
      (s.employees || []).forEach((e) => {
        (e.absences || []).forEach((a) => {
          const id =
            typeof a?.absenceType === "object"
              ? a.absenceType?._id
              : a?.absenceType;
          if (id) set.add(String(id));
        });
      });
    });
    return Array.from(set).map((id) => ({ id, name: id, systemKey: null }));
  }, [absenceRules, shifts]);

  // helper to filter shifts window/schema & ensure employee is on shift
  const relevantShiftsForEmp = (emp) => {
    return shifts.filter((s) => {
      if (!isShiftCompleteForPayroll(s)) return false;

      const d = new Date(s.date);
      if (isNaN(d)) return false;

      const sameMonth = d.getMonth() + 1 === selectedMonth;
      const sameYear = d.getFullYear() === selectedYear;

      const sameSchema =
        !selectedSchema || String(s.timeSchema) === String(selectedSchema);

      const hasEmp =
        Array.isArray(s.employees) &&
        s.employees.some((e) => {
          const empId = getEmployeeId(e);
          if (!empId) return false;
          return String(empId) === String(emp._id);
        });

      return sameMonth && sameYear && sameSchema && hasEmp;
    });
  };

  // ✅ Build sick block:
  // - Always calculate sickHours from absence rules with systemKey "sick"
  // - If karensEnabled, show karens lines and use backend karens summary if available
  // - If karens is OFF, hide karens lines and show only:
  //     - Sjuktimmar
  //     - Betalda timmar (same as sjuktimmar)
  const buildSickBlock = ({ emp, empShifts }) => {
    const { summary } = calculatePayForEmployee({
      employee: emp,
      shifts: empShifts,
      payRates: pays || [],
      vacationPay: vacationPay || {},
      overtimeRules: overtimeRules || [],
      obRules: obRules || [],
      absenceRules: absenceRules || [],
      returnDetailed: true,
    });

    // 1) Identify which absence rule ids are "sick"
    const sickRuleIds = new Set(
      (absenceRules || [])
        .filter((r) => (r?.systemKey || null) === "sick")
        .map((r) => String(r._id))
        .filter(Boolean)
    );

    // 2) Sum sick hours from summary.absenceBreakdown
    let sickHours = 0;
    const breakdown = Array.isArray(summary?.absenceBreakdown)
      ? summary.absenceBreakdown
      : [];

    if (sickRuleIds.size > 0 && breakdown.length > 0) {
      for (const row of breakdown) {
        const rid = String(row?._id || "");
        if (!rid) continue;
        if (sickRuleIds.has(rid)) sickHours += Number(row?.hours || 0);
      }
    } else {
      // Fallback: calculate directly from shifts' absences
      if (sickRuleIds.size > 0) {
        const toMinutes = (str) => {
          const [h, m] = String(str).split(":").map(Number);
          return h * 60 + m;
        };

        for (const s of empShifts) {
          const assignment = (s.employees || []).find((x) => {
            const id = getEmployeeId(x);
            if (!id) return false;
            return String(id) === String(emp._id);
          });
          if (!assignment) continue;

          const startStr = s.startTime;
          const endStr = s.endTime;
          if (!startStr || !endStr) continue;

          const sMin = toMinutes(startStr);
          let eMin = toMinutes(endStr);
          if (eMin <= sMin) eMin += 1440;
          const grossHrs = (eMin - sMin) / 60;

          for (const abs of assignment.absences || []) {
            const typeId = String(
              typeof abs?.absenceType === "object"
                ? abs.absenceType?._id
                : abs?.absenceType || ""
            );
            if (!typeId || !sickRuleIds.has(typeId)) continue;

            let add = 0;
            if (abs.appliesWholeShift) {
              add = grossHrs;
            } else if (abs.from && abs.to) {
              let aFrom = toMinutes(abs.from);
              let aTo = toMinutes(abs.to);
              if (aTo <= aFrom) aTo += 1440;
              if (eMin > 1440 && aTo <= sMin) {
                aFrom += 1440;
                aTo += 1440;
              }
              const overlapStart = Math.max(sMin, aFrom);
              const overlapEnd = Math.min(eMin, aTo);
              const overlap = Math.max(0, overlapEnd - overlapStart);
              add = overlap / 60;
            }
            sickHours += add;
          }
        }
      }
    }

    if (!Number.isFinite(sickHours) || sickHours < 0) sickHours = 0;

    // 3) If karens is OFF => hide karens lines, paid = sick
    if (!karensEnabled) {
      return {
        totalAbsence: Number(summary?.absenceHours || 0),
        sick: {
          sickHours: Number(sickHours || 0),
          karensHours: 0,
          paidSickHours: Number(sickHours || 0),
          karensMaxHours: 0,
          usesKarens: false,
        },
      };
    }

    // 4) Karens is ON => use backend summary if present
    const k = karensByEmployee?.[String(emp._id)];
    const kSummary = k?.summary || null;

    const hasKarensSummary =
      !!kSummary &&
      (kSummary.monthKarensHours !== undefined ||
        kSummary.monthPaidSickHours !== undefined ||
        kSummary.monthKarensMaxHours !== undefined);

    let karensHours = 0;
    let paidSickHours = sickHours;
    let karensMaxHours = 0;

    if (hasKarensSummary) {
      karensHours = Number(kSummary.monthKarensHours || 0);
      paidSickHours = Number(kSummary.monthPaidSickHours || 0);
      karensMaxHours = Number(kSummary.monthKarensMaxHours || 0);
    } else {
      karensHours = 0;
      paidSickHours = sickHours;
      karensMaxHours = 0;
    }

    return {
      totalAbsence: Number(summary?.absenceHours || 0),
      sick: {
        sickHours: Number(sickHours || 0),
        karensHours: Number(karensHours || 0),
        paidSickHours: Number(paidSickHours || 0),
        karensMaxHours: Number(karensMaxHours || 0),
        usesKarens: true,
      },
    };
  };

  // Build table rows
  const tableData = useMemo(() => {
    return filteredEmployees.map((emp) => {
      const empShifts = relevantShiftsForEmp(emp);
      const sickBlock = buildSickBlock({ emp, empShifts });

      // Per-type hours
      const perTypeMap = {};
      empShifts.forEach((s) => {
        const assignment = (s.employees || []).find((x) => {
          const id = getEmployeeId(x);
          if (!id) return false;
          return String(id) === String(emp._id);
        });
        if (!assignment) return;

        (assignment.absences || []).forEach((abs) => {
          const typeId = String(
            typeof abs?.absenceType === "object"
              ? abs.absenceType?._id
              : abs?.absenceType || ""
          );
          if (!typeId) return;

          const toMinutes = (str) => {
            const [h, m] = String(str).split(":").map(Number);
            return h * 60 + m;
          };

          const startStr = s.startTime;
          const endStr = s.endTime;
          if (!startStr || !endStr) return;

          const sMin = toMinutes(startStr);
          let eMin = toMinutes(endStr);
          if (eMin <= sMin) eMin += 1440;
          const grossHrs = (eMin - sMin) / 60;

          let add = 0;
          if (abs.appliesWholeShift) {
            add = grossHrs;
          } else if (abs.from && abs.to) {
            let aFrom = toMinutes(abs.from);
            let aTo = toMinutes(abs.to);
            if (aTo <= aFrom) aTo += 1440;
            if (eMin > 1440 && aTo <= sMin) {
              aFrom += 1440;
              aTo += 1440;
            }
            const overlapStart = Math.max(sMin, aFrom);
            const overlapEnd = Math.min(eMin, aTo);
            const overlap = Math.max(0, overlapEnd - overlapStart);
            add = overlap / 60;
          }

          perTypeMap[typeId] = (perTypeMap[typeId] || 0) + add;
        });
      });

      const perTypeArray = absenceColumns.map((col) =>
        Number(perTypeMap[col.id] || 0)
      );

      return {
        emp,
        totalAbsence: sickBlock.totalAbsence,
        perTypeArray,
        sick: sickBlock.sick,
      };
    });
  }, [
    filteredEmployees,
    shifts,
    selectedMonth,
    selectedYear,
    selectedSchema,
    absenceColumns,
    pays,
    vacationPay,
    overtimeRules,
    obRules,
    absenceRules,
    karensByEmployee,
    karensEnabled,
  ]);

  const minWidth = compactMode
    ? 620
    : 460 + Math.max(0, absenceColumns.length) * 120;

  const fmt = (n) => Number(n || 0).toFixed(2);

  // Simple, readable inline styles (no extra headers/cards)
  const sickBlockStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    lineHeight: 1.15,
    fontSize: 12,
  };

  const sickLineStyle = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    width: "100%",
    maxWidth: 160,
    minWidth: 0,
  };

  const sickLabelStyle = {
    fontWeight: 600,
    opacity: 0.85,
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  };

  const sickValueStyle = {
    flex: "0 0 auto",
    whiteSpace: "nowrap",
    overflow: "visible",
    textOverflow: "clip",
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    minWidth: 30,
  };

  return (
    <div className="payroll-page">
      <Nav />
      <div className="payroll-container">
        <h1 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: 16 }}>
          Frånvaro-översikt
        </h1>

        <div
          className="top-inputs-container"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <div className="search-bar-container" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Sök efter namn, email eller roll..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="schema-select payroll-select"
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
          >
            <option value="">-- Visa alla scheman --</option>
            {timeSchemas.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            className="month-select payroll-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(+e.target.value)}
          >
            {monthsInSwedish.map((m, i) => (
              <option key={i} value={i + 1}>
                {m}
              </option>
            ))}
          </select>

          <select
            className="year-select payroll-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(+e.target.value)}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
              (y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              )
            )}
          </select>

          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 8,
              fontSize: 13,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={compactMode}
              onChange={(e) => setCompactMode(e.target.checked)}
            />
            Kompakt läge (dölj typer)
          </label>
        </div>

        {loading ? (
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        ) : (
          <div className="payroll-grid" style={{ overflowX: "auto" }}>
            {/* HEADER */}
            <div
              className="payroll-header"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth,
              }}
            >
              <span style={{ flex: 2 }}>Namn</span>
              <span style={{ flex: 1, textAlign: "center" }}>Total frånvaro</span>
              <span style={{ flex: 1.35, textAlign: "center" }}>Sjukfrånvaro</span>

              {!compactMode &&
                absenceColumns.map((col) => (
                  <span key={col.id} style={{ flex: 1, textAlign: "center" }}>
                    {col.name}
                  </span>
                ))}
            </div>

            {/* ROWS */}
            {tableData.map(({ emp, totalAbsence, perTypeArray, sick }) => (
              <div
                key={emp._id}
                className="payroll-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth,
                }}
              >
                <span style={{ flex: 2 }}>{emp.name}</span>

                <span style={{ flex: 1, textAlign: "center" }}>
                  {fmt(totalAbsence)}
                </span>

                {/* ✅ Sjukblock: om karens av => visa bara Sjuktimmar + Betalda timmar */}
                <span style={{ flex: 1.35, textAlign: "center" }}>
                  <div style={sickBlockStyle}>
                    <div style={sickLineStyle}>
                      <span style={sickLabelStyle}>Sjuktimmar</span>
                      <span style={sickValueStyle}>{fmt(sick.sickHours)}</span>
                    </div>

                    {karensEnabled ? (
                      <>
                        <div style={sickLineStyle}>
                          <span style={sickLabelStyle}>Karens(använd)</span>
                          <span style={sickValueStyle}>{fmt(sick.karensHours)}</span>
                        </div>
                        <div style={sickLineStyle}>
                          <span style={sickLabelStyle}>Karensram(max)</span>
                          <span style={sickValueStyle}>{fmt(sick.karensMaxHours)}</span>
                        </div>
                        <div style={sickLineStyle}>
                          <span style={sickLabelStyle}>Betalda timmar</span>
                          <span style={sickValueStyle}>{fmt(sick.paidSickHours)}</span>
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </span>

                {!compactMode &&
                  perTypeArray.map((h, i) => (
                    <span
                      key={`${emp._id}-${i}`}
                      style={{ flex: 1, textAlign: "center" }}
                    >
                      {fmt(h)}
                    </span>
                  ))}
              </div>
            ))}
          </div>
        )}

        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage("")}
        />
      </div>
    </div>
  );
};

export default AbsenceOverview;
